/**
 * Step 2's running text (handoff §4.1–§4.3, §19.3): every word tappable, the
 * four word states, phrases by hold-and-drag (mouse drag on desktop), and the
 * selected phrase's handles.
 *
 * Gestures are read here and reported as intents; the page owns the marks:
 * - **Tap** (touch or click): `onTap(word)`. Shift+click is `onTap(word, true)`.
 * - **Long-press** on touch starts a phrase, then the finger extends it across
 *   words and lines; a mouse starts one by dragging. Release reports
 *   `onPhrase(first, last)`, or `onPressRelease(word)` when it never left the word.
 * - **Handles** of the selected phrase move either end: `onResize(first, last)`.
 *
 * Android: the text is not selectable and its context menu is suppressed, so a
 * long-press never opens the OS selection, its handles or a menu. A vertical swipe
 * scrolls, because a touch that moves before the long-press fires is let go to the
 * browser; only a drag that began with a long-press (or on a handle) holds the page
 * still. A selection never crosses a blocked word (ADR-115 Decision 3).
 *
 * Hit areas: a word's band is the full line height, and a touch between words goes
 * to the nearest one, so short dense words stay tappable.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { AnalysedWord } from '../../types'
import { clampRange, type SepLook, type WordLook } from '../../utils/textMarks'

const LONG_PRESS_MS = 450
const TOUCH_SLOP = 10
const MOUSE_SLOP = 4
const EDGE = 56 // autoscroll zone at the scroller's visible top and bottom

export interface DraftRange {
  first: number
  last: number
}

interface Props {
  body: string
  words: AnalysedWord[]
  wordLooks: WordLook[]
  sepLooks: SepLook[]
  /** The word the panel controls: the last-tapped frame. */
  selectedWord: number | null
  /** The selected phrase: its handles show. */
  selectedPhrase: { id: string; first: number; last: number } | null
  /** Tap path, waiting for the phrase's last word: the anchor shows the start handle. */
  waitingAnchor: number | null
  /** Words a new phrase from `anchor` (or a resize of `phrase`) may not cross. */
  blocked: (opts: { anchor?: number; phrase?: string }) => boolean[]
  onTap: (word: number, shift: boolean) => void
  onPressRelease: (word: number) => void
  onPhrase: (first: number, last: number) => void
  onResize: (id: string, first: number, last: number) => void
  desktop: boolean
}

type Mode =
  | { kind: 'idle' }
  | { kind: 'pending'; pointerId: number; x: number; y: number; word: number; mouse: boolean; shift: boolean; noDrag?: boolean }
  | { kind: 'scroll' }
  | { kind: 'drag'; pointerId: number; anchor: number; block: boolean[] }
  | { kind: 'handle'; pointerId: number; id: string; end: 'start' | 'end'; fixed: number; block: boolean[] }

interface Rect { left: number; top: number; right: number; bottom: number }

function scrollParent(el: HTMLElement | null): HTMLElement {
  for (let p = el?.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) return p
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement
}

/** The part of the scroller the learner can see: never under the keyboard (visual viewport). */
function visibleBand(scroller: HTMLElement): { top: number; bottom: number } {
  const vv = window.visualViewport
  const viewTop = vv ? vv.offsetTop : 0
  const viewBottom = vv ? vv.offsetTop + vv.height : window.innerHeight
  if (scroller === document.scrollingElement || scroller === document.documentElement) {
    return { top: viewTop, bottom: viewBottom }
  }
  const r = scroller.getBoundingClientRect()
  return { top: Math.max(r.top, viewTop), bottom: Math.min(r.bottom, viewBottom) }
}

const RT_CSS = `
.rt{position:relative;color:var(--text-primary);font-family:var(--font-content);white-space:pre-wrap;overflow-wrap:break-word;
  user-select:none;-webkit-user-select:none;-webkit-touch-callout:none;-webkit-tap-highlight-color:transparent;touch-action:pan-y}
.rt-w{padding:1px 2px;border-radius:4px;cursor:pointer}
.rt-s{padding:1px 0}
.rt-w.c,.rt-s.c{background:var(--cefr-b1-bg)}
.rt-w.g,.rt-s.g{text-decoration-line:underline;text-decoration-style:dashed;text-decoration-color:var(--accent);
  text-decoration-thickness:2px;text-underline-offset:6px;text-decoration-skip-ink:none}
.rt-w.first{border-radius:4px 0 0 4px}.rt-w.mid{border-radius:0}.rt-w.last{border-radius:0 4px 4px 0}
.rt-w.sel{outline:2px solid var(--accent);outline-offset:1px}
.rt-w.k{color:var(--text-secondary);box-shadow:inset 0 -2px 0 var(--border)}
@media (hover:hover){.rt-w.plain:hover{background:var(--bg-secondary)}}
.rt-h{position:absolute;width:44px;height:44px;margin-left:-22px;touch-action:none;cursor:ew-resize;z-index:1}
.rt-h-bar{position:absolute;left:21px;width:2px;height:24px;background:var(--accent)}
.rt-h-dot{position:absolute;left:16px;width:12px;height:12px;border-radius:50%;background:var(--accent)}
`

export default function RunningText({
  body, words, wordLooks, sepLooks, selectedWord, selectedPhrase, waitingAnchor, blocked,
  onTap, onPressRelease, onPhrase, onResize, desktop,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null)
  const wordEls = useRef<(HTMLSpanElement | null)[]>([])
  const mode = useRef<Mode>({ kind: 'idle' })
  const timer = useRef<number | null>(null)
  const rects = useRef<Rect[][]>([])
  const pointer = useRef<{ x: number; y: number } | null>(null)
  const raf = useRef<number | null>(null)
  const [draft, setDraft] = useState<DraftRange | null>(null)
  const draftRef = useRef<DraftRange | null>(null)
  const startHandle = useRef<HTMLDivElement>(null)
  const endHandle = useRef<HTMLDivElement>(null)

  const lineHeight = desktop ? 40 : 35

  const setDraftBoth = (d: DraftRange | null) => {
    draftRef.current = d
    setDraft(d)
  }

  /** Word rectangles relative to the text, cached for a gesture (scrolling moves the text, not them). */
  const measure = useCallback(() => {
    const root = rootRef.current
    if (!root) return
    const base = root.getBoundingClientRect()
    rects.current = wordEls.current.map((el) =>
      el
        ? [...el.getClientRects()].map((r) => ({
            left: r.left - base.left, top: r.top - base.top, right: r.right - base.left, bottom: r.bottom - base.top,
          }))
        : [],
    )
  }, [])

  /** The word whose line band holds the point, else the nearest; null outside every line. */
  const wordAt = useCallback((clientX: number, clientY: number, cached = false): number | null => {
    const root = rootRef.current
    if (!root) return null
    if (!cached) measure()
    const base = root.getBoundingClientRect()
    const x = clientX - base.left
    const y = clientY - base.top
    let best: number | null = null
    let bestScore = Infinity
    rects.current.forEach((list, i) => {
      for (const r of list) {
        const cy = (r.top + r.bottom) / 2
        const dy = Math.max(0, Math.abs(y - cy) - lineHeight / 2)
        const dx = x < r.left ? r.left - x : x > r.right ? x - r.right : 0
        const score = dy * 10_000 + dx
        if (score < bestScore) {
          bestScore = score
          best = i
        }
      }
    })
    return best
  }, [lineHeight, measure])

  // ── Handles: where the selected (or drafted, or waiting) range starts and ends ─
  const range: DraftRange | null =
    draft ?? (selectedPhrase ? { first: selectedPhrase.first, last: selectedPhrase.last } : null)
  const startWord = range ? range.first : waitingAnchor
  const endWord = range ? range.last : null

  // Placed from the words' boxes after layout, straight on the elements (no render loop).
  useLayoutEffect(() => {
    const root = rootRef.current
    if (startWord === null || !root) return
    const place = () => {
      const base = root.getBoundingClientRect()
      const s = wordEls.current[startWord]?.getClientRects()[0]
      const eRects = endWord !== null ? wordEls.current[endWord]?.getClientRects() : undefined
      const e = eRects && eRects.length ? eRects[eRects.length - 1] : undefined
      const sh = startHandle.current
      const eh = endHandle.current
      if (sh) {
        sh.style.visibility = s ? 'visible' : 'hidden'
        if (s) Object.assign(sh.style, { left: `${s.left - base.left}px`, top: `${s.top - base.top - 14}px` })
      }
      if (eh) {
        eh.style.visibility = e ? 'visible' : 'hidden'
        if (e) Object.assign(eh.style, { left: `${e.right - base.left}px`, top: `${e.bottom - base.top - 30}px` })
      }
    }
    place()
    const ro = new ResizeObserver(place)
    ro.observe(root)
    return () => ro.disconnect()
  }, [startWord, endWord, body, desktop])

  // A newly selected phrase's handles are scrolled into the visible band, above the panel and keyboard.
  useEffect(() => {
    if (!selectedPhrase || draft) return
    const root = rootRef.current
    const last = wordEls.current[selectedPhrase.last]
    const first = wordEls.current[selectedPhrase.first]
    if (!root || !last || !first) return
    const scroller = scrollParent(root)
    const band = visibleBand(scroller)
    const lr = last.getBoundingClientRect()
    const fr = first.getBoundingClientRect()
    const below = lr.bottom + 30 - band.bottom // the end handle's dot hangs under the word
    const above = band.top - (fr.top - 30)
    if (below > 0) scroller.scrollTop += below
    else if (above > 0) scroller.scrollTop -= above
  }, [selectedPhrase, draft])

  // ── Gestures ───────────────────────────────────────────────────────────────
  const clearTimer = () => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = null
  }

  const stopAutoscroll = () => {
    if (raf.current !== null) cancelAnimationFrame(raf.current)
    raf.current = null
  }

  const extendTo = useCallback((clientX: number, clientY: number) => {
    const m = mode.current
    const target = wordAt(clientX, clientY, true)
    if (target === null) return
    if (m.kind === 'drag') {
      const [first, last] = clampRange(m.anchor, target, m.block)
      setDraftBoth({ first, last })
    } else if (m.kind === 'handle') {
      // The moved end may not pass the fixed one: a phrase keeps two words at least.
      const bounded = m.end === 'start' ? Math.min(target, m.fixed - 1) : Math.max(target, m.fixed + 1)
      const [first, last] = clampRange(m.fixed, bounded, m.block)
      if (first !== last) setDraftBoth({ first, last })
    }
  }, [wordAt])

  // One autoscroll step per frame while the finger sits near the visible top or bottom edge.
  const autoscrollStep = useRef<() => void>(() => {})
  const autoscroll = useCallback(() => {
    raf.current = null
    const m = mode.current
    const p = pointer.current
    const root = rootRef.current
    if ((m.kind !== 'drag' && m.kind !== 'handle') || !p || !root) return
    const scroller = scrollParent(root)
    const band = visibleBand(scroller)
    const speed =
      p.y > band.bottom - EDGE ? Math.min(16, (p.y - (band.bottom - EDGE)) / 3)
      : p.y < band.top + EDGE ? -Math.min(16, (band.top + EDGE - p.y) / 3)
      : 0
    if (speed !== 0) {
      scroller.scrollTop += speed
      extendTo(p.x, p.y)
      raf.current = requestAnimationFrame(() => autoscrollStep.current())
    }
  }, [extendTo])
  useEffect(() => {
    autoscrollStep.current = autoscroll
  }, [autoscroll])

  /** Starts a phrase at `anchor`, unless the anchor is itself inside a phrase or a gap: then it stays a tap. */
  const beginDrag = (m: Extract<Mode, { kind: 'pending' }>) => {
    const block = blocked({ anchor: m.word })
    if (block[m.word]) {
      mode.current = { ...m, noDrag: true }
      return
    }
    measure()
    mode.current = { kind: 'drag', pointerId: m.pointerId, anchor: m.word, block }
    setDraftBoth({ first: m.word, last: m.word })
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || mode.current.kind !== 'idle') return
    // A touch on a word is that word; between words, the nearest one (full line-height bands).
    const direct = (e.target as HTMLElement).closest?.('[data-word]')
    const word = direct ? Number(direct.getAttribute('data-word')) : wordAt(e.clientX, e.clientY)
    if (word === null) return
    const mouse = e.pointerType === 'mouse'
    mode.current = { kind: 'pending', pointerId: e.pointerId, x: e.clientX, y: e.clientY, word, mouse, shift: e.shiftKey }
    if (!mouse) {
      timer.current = window.setTimeout(() => {
        timer.current = null
        const m = mode.current
        if (m.kind === 'pending') beginDrag(m)
      }, LONG_PRESS_MS)
    }
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const m = mode.current
    if (m.kind === 'pending' && e.pointerId === m.pointerId) {
      const moved = Math.hypot(e.clientX - m.x, e.clientY - m.y)
      if (!m.mouse && moved > TOUCH_SLOP) {
        // A swipe before the long-press: the browser scrolls, nothing is marked.
        clearTimer()
        mode.current = { kind: 'scroll' }
      } else if (m.mouse && moved > MOUSE_SLOP && !m.noDrag) {
        rootRef.current?.setPointerCapture?.(e.pointerId)
        beginDrag(m)
        if (mode.current.kind === 'drag') extendTo(e.clientX, e.clientY)
      }
      return
    }
    if ((m.kind === 'drag' || m.kind === 'handle') && e.pointerId === m.pointerId) {
      pointer.current = { x: e.clientX, y: e.clientY }
      extendTo(e.clientX, e.clientY)
      if (raf.current === null) raf.current = requestAnimationFrame(autoscroll)
    }
  }

  const finish = (e: React.PointerEvent) => {
    const m = mode.current
    clearTimer()
    stopAutoscroll()
    mode.current = { kind: 'idle' }
    pointer.current = null
    const d = draftRef.current
    setDraftBoth(null)
    if (m.kind === 'pending' && e.pointerId === m.pointerId) onTap(m.word, m.shift)
    else if (m.kind === 'drag' && d) {
      if (d.first === d.last) {
        if (m.anchor === d.first && e.pointerType === 'mouse') onTap(m.anchor, false)
        else onPressRelease(m.anchor)
      } else onPhrase(d.first, d.last)
    } else if (m.kind === 'handle' && d) onResize(m.id, d.first, d.last)
  }

  const cancel = () => {
    clearTimer()
    stopAutoscroll()
    mode.current = { kind: 'idle' }
    setDraftBoth(null)
  }

  // Holding the page still only while a long-press drag or a handle drag is under way.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const hold = (ev: TouchEvent) => {
      const k = mode.current.kind
      if (k === 'drag' || k === 'handle') ev.preventDefault()
    }
    root.addEventListener('touchmove', hold, { passive: false })
    return () => root.removeEventListener('touchmove', hold)
  }, [])

  useEffect(() => () => { clearTimer(); stopAutoscroll() }, [])

  const onHandleDown = (end: 'start' | 'end') => (e: React.PointerEvent<HTMLDivElement>) => {
    if (!selectedPhrase || e.button !== 0) return
    e.stopPropagation()
    e.preventDefault()
    e.currentTarget.setPointerCapture?.(e.pointerId)
    measure()
    const fixed = end === 'start' ? selectedPhrase.last : selectedPhrase.first
    mode.current = {
      kind: 'handle', pointerId: e.pointerId, id: selectedPhrase.id, end, fixed,
      block: blocked({ phrase: selectedPhrase.id }),
    }
    setDraftBoth({ first: selectedPhrase.first, last: selectedPhrase.last })
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const inDraft = (i: number) => draft !== null && i >= draft.first && i <= draft.last
  const wordClass = (i: number) => {
    const look = wordLooks[i]
    if (inDraft(i) && draft) {
      const edge = draft.first === draft.last ? '' : i === draft.first ? ' first' : i === draft.last ? ' last' : ' mid'
      return `rt-w c${edge}`
    }
    const cls = ['rt-w']
    const marked = look.card || look.gap || look.phrase !== null
    if (look.card || i === waitingAnchor) cls.push('c')
    if (look.gap) cls.push('g')
    if (look.carded) cls.push('k')
    if (look.edge && look.edge !== 'only') cls.push(look.edge)
    if (!marked && i !== waitingAnchor) cls.push('plain')
    if ((i === selectedWord && !selectedPhrase) || i === waitingAnchor) cls.push('sel')
    return cls.join(' ')
  }
  const sepClass = (i: number) => {
    if (draft && i >= draft.first && i < draft.last) return 'rt-s c'
    const s = sepLooks[i]
    return `rt-s${s.fill ? ' c' : ''}${s.underline ? ' g' : ''}`
  }

  const parts: React.ReactNode[] = []
  if (words.length === 0) parts.push(body)
  else parts.push(body.slice(0, words[0].start))
  words.forEach((w, i) => {
    const look = wordLooks[i]
    parts.push(
      <span
        key={`w${i}`}
        ref={(el) => { wordEls.current[i] = el }}
        className={wordClass(i)}
        data-word={i}
        data-card={look.card || undefined}
        data-gap={look.gap || undefined}
        data-phrase={look.phrase ?? undefined}
        data-carded={look.carded || undefined}
      >
        {body.slice(w.start, w.end)}
      </span>,
    )
    const next = words[i + 1]
    const sep = body.slice(w.end, next ? next.start : body.length)
    if (sep) {
      parts.push(next ? <span key={`s${i}`} className={sepClass(i)}>{sep}</span> : sep)
    }
  })

  return (
    <div
      ref={rootRef}
      className="rt"
      data-testid="running-text"
      style={{ fontSize: desktop ? 19 : 18, lineHeight: `${lineHeight}px` }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={cancel}
      onContextMenu={(e) => e.preventDefault()}
    >
      <style>{RT_CSS}</style>
      {parts}
      {startWord !== null && (
        <div
          ref={startHandle}
          className="rt-h"
          data-testid="handle-start"
          aria-hidden
          onPointerDown={onHandleDown('start')}
          style={{ visibility: 'hidden', pointerEvents: selectedPhrase ? 'auto' : 'none' }}
        >
          <span className="rt-h-dot" style={{ top: 2 }} />
          <span className="rt-h-bar" style={{ top: 12 }} />
        </div>
      )}
      {endWord !== null && (
        <div
          ref={endHandle}
          className="rt-h"
          data-testid="handle-end"
          aria-hidden
          onPointerDown={onHandleDown('end')}
          style={{ visibility: 'hidden', pointerEvents: selectedPhrase ? 'auto' : 'none' }}
        >
          <span className="rt-h-bar" style={{ top: 6 }} />
          <span className="rt-h-dot" style={{ top: 28 }} />
        </div>
      )}
    </div>
  )
}
